import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import { SLOW_MS } from '../queries';
import { adsKeys, notReadyOf, sortAds, statusToast } from './lib.js';
import type { AdStatus, OpsAd } from './types';

export function useOpsAds() {
  return useQuery({ queryKey: adsKeys.list, queryFn: async () => sortAds((await api.opsAds()).items ?? []), refetchInterval: SLOW_MS });
}

/** Put a saved ad into the cached list (new ones on top). */
export function useStoreAd() {
  const qc = useQueryClient();
  return (ad: OpsAd) =>
    qc.setQueryData<OpsAd[]>(adsKeys.list, (xs) => sortAds(xs ? (xs.some((x) => x.id === ad.id) ? xs.map((x) => (x.id === ad.id ? ad : x)) : [ad, ...xs]) : [ad]));
}

export type NotReady = { ad: OpsAd; missing: string[] };

/**
 * Go live / pause / archive / back to draft. A 409 not_ready comes back as `notReady` (the ad and
 * its missing[]) for the page to show as a checklist; anything else is a toast.
 */
export function useAdStatus() {
  const qc = useQueryClient();
  const toast = useToast();
  const store = useStoreAd();
  const [notReady, setNotReady] = useState<NotReady | null>(null);
  const m = useMutation({
    mutationFn: (v: { ad: OpsAd; to: AdStatus }) => api.updateOpsAd(v.ad.id, { status: v.to }),
    onMutate: () => setNotReady(null),
    onSuccess: (saved, v) => {
      store(saved);
      toast(statusToast(saved?.headline ?? v.ad.headline, v.to));
    },
    onError: (err, v) => {
      const missing = notReadyOf(err);
      if (missing) setNotReady({ ad: v.ad, missing: missing.length ? missing : v.ad.missing });
      else toast(errorMessage(err, "Couldn't change the ad. Try again."), 'error');
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: adsKeys.all }),
  });
  return { ...m, notReady, clearNotReady: () => setNotReady(null) };
}
